import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { type DashboardProduct, portalProductsFor } from '../../dashboard/products';
import { fetchAllPortalInstances, type PortalInstance } from '../../portal/portalClient';
import { instanceQueryKey } from './useInstanceLabel';
import { usePortalAccess } from './usePortalAccess';

const STALE_TIME = 5 * 60 * 1000;

/** Shared, so an omitted `products` is the same reference every render. */
const EMPTY_PRODUCTS: DashboardProduct[] = [];

export type PortalInstanceDirectory = {
  instances: PortalInstance[];
  /** Deduplicated and sorted. */
  customers: string[];
  instancesOfCustomer: (customer: string) => PortalInstance[];
  /** A deployment's own name, without its account. */
  nameOf: (id: string) => string;
  status: 'loading' | 'success' | 'error';
  isPortalSource: boolean;
};

/**
 * The whole product-scoped instance list, held once and read by both cascade steps.
 *
 * The full list rather than a page because the portal has **no `account` filter**: neither "which
 * customers exist" nor "which instances are this customer's" can be asked server-side.
 */
export const usePortalInstanceDirectory = (products: DashboardProduct[] = EMPTY_PRODUCTS): PortalInstanceDirectory => {
  const { baseUrl, token, canQuery, isPortalSource } = usePortalAccess();
  const queryClient = useQueryClient();

  const portalProducts = useMemo(() => portalProductsFor(products), [products]);

  const query = useQuery({
    queryKey: ['portal-instance-directory', baseUrl, 'products', portalProducts],
    queryFn: async () => {
      const instances = await fetchAllPortalInstances({ baseUrl, token, products: portalProducts });
      // Primes the shared by-id cache, so a picked deployment needs no second request.
      for (const instance of instances) {
        if (instance.id) queryClient.setQueryData(instanceQueryKey(baseUrl, instance.id), instance);
      }
      return instances;
    },
    enabled: canQuery,
    staleTime: STALE_TIME,
    retry: false,
  });

  const instances = useMemo(() => query.data ?? [], [query.data]);

  const instancesByCustomer = useMemo(() => {
    const groups = new Map<string, PortalInstance[]>();
    for (const instance of instances) {
      const customer = instance.account?.trim();
      // No account means unreachable through this cascade; '' would render as a blank row.
      if (!customer || !instance.id) continue;
      const existing = groups.get(customer);
      if (existing) existing.push(instance);
      else groups.set(customer, [instance]);
    }
    return groups;
  }, [instances]);

  const customers = useMemo(
    () => [...instancesByCustomer.keys()].sort((a, b) => a.localeCompare(b)),
    [instancesByCustomer],
  );

  // Off the whole list, not one customer's: a stale link can name someone else's deployment.
  const namesById = useMemo(() => {
    const names = new Map<string, string>();
    for (const instance of instances) {
      if (instance.id) names.set(instance.id, instance.name?.trim() || instance.id);
    }
    return names;
  }, [instances]);

  return useMemo(
    () => ({
      instances,
      customers,
      instancesOfCustomer: (customer: string) => instancesByCustomer.get(customer.trim()) ?? [],
      nameOf: (id: string) => namesById.get(id) ?? id,
      // Nothing to wait for off the portal path; 'loading' would hang the cascade.
      status: !isPortalSource
        ? ('success' as const)
        : query.isError
          ? ('error' as const)
          : query.isPending
            ? ('loading' as const)
            : ('success' as const),
      isPortalSource,
    }),
    [instances, customers, instancesByCustomer, namesById, isPortalSource, query.isError, query.isPending],
  );
};
